// The bridge the experience needs and React cannot give it (PRD 45).
//
// WHY A MODULE-LEVEL STORE RATHER THAN A CONTEXT: the sidebar has to be
// filtered by the experience, and WebNavigation is mounted in AppShell — the
// PARENT route of BusinessShell, which is where BusinessProvider lives. So the
// nav renders strictly above the only component that knows which business is
// active, and no context that BusinessProvider owns can reach it.
//
// The same problem already has a house answer: businessReadOnlyNotifier and
// businessPeriodLockNotifier bridge from outside the React tree into the
// shell. This is that pattern with a value attached rather than just an event,
// so a late subscriber reads the current state instead of waiting for the next
// change.
//
// BusinessContext publishes (it owns "which business"); the nav subscribes.
// Nothing else writes here.

import { BusinessExperience } from '../types/businessApi';

export interface BusinessExperienceState {
  /** Null until the first fetch settles, and while no business is active. */
  experience: BusinessExperience | null;
  /** True once a fetch has settled for the current business, success or not. */
  hasLoaded: boolean;
}

type Listener = (state: BusinessExperienceState) => void;

const EMPTY: BusinessExperienceState = { experience: null, hasLoaded: false };

let state: BusinessExperienceState = EMPTY;
let listeners = new Set<Listener>();

export function getBusinessExperienceState(): BusinessExperienceState {
  return state;
}

/** Subscribe to experience changes; returns the unsubscribe function. */
export function subscribeBusinessExperience(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Publish a resolved experience. Called by BusinessContext after a fetch and
 * after every save, so the nav re-filters without a second request.
 */
export function setBusinessExperience(experience: BusinessExperience | null): void {
  state = { experience, hasLoaded: true };
  listeners.forEach(listener => listener(state));
}

/**
 * Back to "unknown" — on sign-out and on a business switch, BEFORE the new
 * fetch starts. Without this the nav would briefly filter the incoming
 * business by the outgoing one's experience, which is how a menu row
 * disappears for a moment and looks like a bug.
 */
export function clearBusinessExperience(): void {
  state = EMPTY;
  listeners.forEach(listener => listener(state));
}

/** Test hook — drops listeners and state. */
export function resetBusinessExperienceStore(): void {
  state = EMPTY;
  listeners = new Set();
}
